import { logD, logE } from "@blacknut/logging/dist";
import { Observable, Observer, Subject, Subscription } from "rxjs";

const TAG = "WS";
const WEBSOCKET_URL = "127.0.0.1";
const WEBSOCKET_PORT = "2280";
export enum GameEventType {
  STARTED = "started",
  STOPPED = "stopped",
  STATISTICS = "statistics",
}
export interface GameEvent {
  type: GameEventType;
  data?: any;
}

/** @deprecated */
export class WebSocketService {
  public streamId: string; // TODO remove
  private wsIp: string;

  private wsPort: string;
  private wsProtocol: string = "lws-bidirectional-protocol";

  private url: string = "";
  private retry: boolean = false;
  private timeout: any;
  private socket: Subject<any> | null;
  private socketSubscription: Subscription;

  // tslint:disable-next-line:variable-name
  private _gameEvents: Subject<GameEvent> = new Subject();

  constructor() {
    this.wsIp = WEBSOCKET_URL;
    this.wsPort = WEBSOCKET_PORT;
    this.url = "ws://" + this.wsIp + ":" + this.wsPort;
  }

  /**
   * This function is used to initialize Web socket parameters
   */
  public init(streamId: string) {
    logD(TAG, "init %s", streamId);
    this.streamId = streamId;
    this.retry = true;
    this.connect(this.url, this.wsProtocol);
  }

  public onGameEvent() {
    return this._gameEvents.asObservable();
  }

  public stop() {
    logD(TAG, "stop");
    this.onClose({ code: 1000 });
  }

  private connect(url: string, wsProtocol: string) {
    if (this.socketSubscription) {
      this.socketSubscription.unsubscribe();
    }
    this.socket = null;

    const openObs = {
      next: (e: any) => {
        this.onOpen(e);
      },
      // tslint:disable-next-line:no-empty
      error: () => {},
      // tslint:disable-next-line:no-empty
      complete: () => {},
    };

    this.socket = this.create(url, wsProtocol, openObs);
    this.socketSubscription = this.socket.subscribe(
      (e) => this.onMessage(e),
      (e) => this.onError(e),
      () => this.onClose(),
    );
    logD(TAG, "Successfully connected: " + url);
  }

  private create(
    url: string,
    protocol: string,
    openObserver: Observer<any>,
  ): Subject<MessageEvent> {
    const ws = new WebSocket(url, protocol);

    const observer = {
      next: (data: any) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(data);
        }
      },
    };

    // Handle the data
    const observable = Observable.create((obs: Observer<any>) => {
      // Handle open
      if (openObserver) {
        ws.onopen = openObserver.next.bind(openObserver);
      }

      // Handle messages
      ws.onmessage = obs.next.bind(obs);
      ws.onerror = obs.error.bind(obs);
      ws.onclose = obs.complete.bind(obs);

      // Return way to unsubscribe
      // return ws.close.bind(ws);
    });

    return Subject.create(observer, observable);
  }

  private onMessage(msg: any) {
    logD(TAG, "onMessage %o", { msg });

    try {
      const requestData = JSON.parse(msg.data);
      if (requestData.method === "gameplay") {
        switch (requestData.params.status) {
          case "game_started":
            this._gameEvents.next({ type: GameEventType.STARTED });
            break;
          case "game_stopped":
            this._gameEvents.next({
              type: GameEventType.STOPPED,
              data: { error: requestData.params.error },
            });
            break;
        }
      } else if (requestData.method === "streamMetrics") {
        this._gameEvents.next({
          type: GameEventType.STATISTICS,
          data: requestData.params,
        });
      }
    } catch (e) {
      logE(TAG, "Caught error parsing data %o", msg.data, e);
    }
  }

  private onOpen(event: any) {
    this.retry = false;
    logD(TAG, "onOpen %o", { event });
  }

  private onError(event: any) {
    logD(TAG, "onError %o", { event });
    if (this.retry) {
      this.timeout = setTimeout(() => {
        logD(TAG, "Retry");
        this.connect(this.url, this.wsProtocol);
      }, 1000);
    }
  }

  private onClose(event?: any) {
    if (event && event.code === 1000) {
      logD(TAG, "onCloseNormaly %o", { event });
      this.socket = null;
      this.retry = false;
      this.timeout = null;
      clearTimeout(this.timeout);
    } else {
      logD(TAG, "onCloseAbnormaly %o", { event });
    }
  }
}

export default new WebSocketService();
